import { t, type TranslationKey } from "@/lib/i18n";
import { ApiError } from "./client";

const errorMessages: Record<string, TranslationKey> = {
  logout_not_confirmed: "header.logout_failed",
  invalid_email: "common.error_invalid_email",
  invalid_password: "common.error_invalid_password",
  unknown_user: "common.error_unknown_user",
  exists_email: "common.error_exists_email",
  invalid_invite_code: "common.error_invalid_invite_code",
  not_have_money: "common.error_not_have_money",
  already_owned: "common.error_already_owned",
  already_saled: "common.error_already_saled",
  is_sold: "common.error_is_sold",
  image_not_find: "common.error_image_not_find",
  own_not_find: "common.error_own_not_find",
  u_cant_buy: "common.error_u_cant_buy",
  u_cant_withdraw: "common.error_u_cant_withdraw",
  invalid_amount: "common.error_invalid_amount",
  invalid_method: "common.error_invalid_method",
  unsupported_in_sale: "common.error_currency_in_sale",
  invalid_details: "common.error_invalid_details",
  invalid_bank: "common.error_invalid_bank",
  small_balance: "common.error_not_have_money",
  small_payment_amount: "common.error_small_payment_amount",
  unavailable_method: "common.error_unavailable_method",
  "unavailable method": "common.error_unavailable_method",
  invalid_code: "common.error_invalid_code",
  already_verified: "common.error_already_verified",
  unverified: "common.error_unverified",
  unauthorized: "common.error_unauthorized",
  auction_active: "market.auction_active_purchase",
  auction_not_found: "common.error_auction_not_found",
  auction_not_started: "common.error_auction_not_started",
  auction_ended: "common.error_auction_ended",
  bid_too_low: "common.error_bid_too_low",
  buy_now_unavailable: "common.error_buy_now_unavailable",
  blocked_by_protection: "common.error_blocked_by_protection",
  service_temporarily_unavailable: "common.error_service_unavailable",
  invalid_api_response: "common.error_invalid_api_response",
  request_timeout: "common.error_timeout",
  network_error: "common.error_network"
};

/**
 * Translation key for an error. Prefer storing this key (or the error itself) in UI state and translating at
 * render time, so an already-shown message follows a later language switch.
 */
export function getUserFacingErrorKey(error: unknown): TranslationKey {
  if (error instanceof DOMException && error.name === "AbortError") return "common.error_aborted";
  if (error instanceof DOMException && error.name === "TimeoutError") return "common.error_timeout";

  if (error instanceof ApiError) {
    if (error.status === 401 || (error.status === 403 && error.code.toLowerCase().includes("not authenticated"))) return "common.error_unauthorized";
    const message = errorMessages[error.code];
    if (message) return message;
    if (error.status === 403) return "auth.partial_unavailable";
    if (error.status === 429) return "common.error_rate_limited";
    if (error.status === 404) return "common.error_not_found";
    if (error.status === 409) return "common.error_conflict";
    if (error.status === 422) return "common.error_unprocessable";
    if (error.status >= 500) return "common.error_service_unavailable";
    return errorMessages[error.code] ?? "common.error_request_failed";
  }

  return "common.error_unexpected";
}

/** Error text in the currently selected language. */
export function getUserFacingError(error: unknown) {
  return t(getUserFacingErrorKey(error));
}
